import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Carousel, Column, Container, List, Row } from '@pella/sash-framework';
import { Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import CardRenderer from '../../../common/card-types/card-renderer';
import GalleryModal from '../../articles/gallery-modal';

const Partner = ({ data, className }) => {
	const [isGalleryOpen, setIsGalleryOpen] = useState(false);
	const [activeImage, setActiveImage] = useState(null);
	const hasCarousel = isValidArray(data?.carousel);
	const isArticle = hasCarousel && data.carousel.find((card) => card.seo?.description) ? true : false;

	const onImageClick = (image) => {
		setActiveImage(image);
		setIsGalleryOpen(true);
	};

	return (
		<div
			data-testid='branch-partner'
			className={`branchPartner py-8 md:py-9 xl:py-10 ${className || ''}`}
		>
			<Container className='max-md:!px-0'>
				<Row className='space-y-8 max-md:!mx-0 md:space-y-0'>
					<Column
						xs={12}
						md={hasCarousel ? 4 : null}
						xl={hasCarousel ? 6 : null}
						className='max-md:!px-6'
					>
						<Row align='center'>
							<Column
								xs={12}
								md={hasCarousel ? null : 'fluid'}
								className={`space-y-4 ${hasCarousel ? 'mb-8 md:mb-9 xl:mb-10' : 'mb-8 md:mb-0'}`}
								data-testid='branch-partner-content'
							>
								{isValidArray(data?.logo) && (
									<Image
										data={data.logo[0]}
										className='h-[66px] md:h-[74px] xl:h-[82px]'
									/>
								)}
								{data?.heading && <h2 className='text-h3 m-0'>{data.heading}</h2>}
								{data?.description && <p className='text-subtitle-2'>{data.description}</p>}

								{isValidArray(data?.badge) && (
									<List
										bullets='none'
										layout='inline'
										className='gap-1'
									>
										{data.badge.map((badge, index) => (
											<li key={`badge-${index}`}>
												<Badge
													color='black'
													size='sm'
												>
													{badge}
												</Badge>
											</li>
										))}
									</List>
								)}
							</Column>

							<Column
								xs={12}
								md={hasCarousel ? null : 'fluid'}
								className='max-md:!px-6'
								data-testid='branch-partner-contact'
							>
								<div className={`partnerContact grid-col-1 row-start-2 ${hasCarousel ? 'md:row-start-3' : 'md:grid-col-2 md:row-start-1'}`}>
									{isValidArray(data?.contact) && (
										<List
											bullets='none'
											className='space-y-4'
										>
											{data.contact.map((contact, index) => (
												<li
													key={`contact-${index}`}
													dangerouslySetInnerHTML={{ __html: contact }}
													className='[&>*]:flex [&>*]:items-start [&>*]:gap-x-2'
												/>
											))}
										</List>
									)}
								</div>
							</Column>
						</Row>
					</Column>

					{hasCarousel && (
						<Column
							xs={12}
							md={8}
							xl={6}
							className='max-md:!px-0'
							data-testid='branch-partner-carousel'
						>
							<Carousel
								slidePadding={16}
								slidesToShow={{ xs: 1, sm: 1.6, md: 1.2, lg: 1.65, xl: 1 }}
								arrowsClassName='max-md:!hidden [&>li:first-child>button]:left-0 [&>li:last-child>button]:right-0'
								className='scroll-px-6 px-6 before:z-2 max-md:before:content-none md:scroll-px-24 md:px-24 xl:scroll-px-24 xl:px-24 2xl:scroll-px-40 2xl:px-40'
								center={false}
								showSlideHints
								showArrows
								showDots
							>
								{data.carousel.map((card, index) => {
									if (isArticle) {
										const cardDescription = card.top_content?.subheading || card.seo?.description;

										return (
											<CardRenderer
												key={index}
												data={{
													card_type: 'standard',
													image: [{ image: card.seo?.thumbnail }],
													ctas: [{ link: { href: card.url }, aria: card.top_content?.heading }],
													heading: card.top_content?.heading,
													description: cardDescription?.length > 80 ? cardDescription.substring(0, 80) + '...' : cardDescription,
													seo_tag: card.top_content?.heading,
												}}
												showCtas={false}
											/>
										);
									} else {
										return (
											<button
												key={index}
												className='h-full w-full'
												onClick={() => onImageClick(card.image?.url)}
											>
												<Image
													data={card}
													className='!block w-full'
												/>
											</button>
										);
									}
								})}
							</Carousel>

							{!isArticle && (
								<GalleryModal
									images={data.carousel}
									isOpen={isGalleryOpen}
									activeImage={activeImage}
									onClose={() => {
										setIsGalleryOpen(false);
										setActiveImage(null);
									}}
								/>
							)}
						</Column>
					)}
				</Row>
			</Container>
		</div>
	);
};

Partner.propTypes = {
	data: PropTypes.object.isRequired,
	className: PropTypes.string,
};

export default Partner;
